import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Форма создания заметки — один источник для плавающей кнопки «Заметка» и для
// кнопки «Новая заметка» в списке. Раньше форма жила внутри QuickNoteButton, и
// у списка не было способа создать заметку своей кнопкой.
import { useEffect, useState, type ReactElement } from 'react';
import { Alert, Checkbox, Input, Modal, Tag, Typography } from 'antd';

import {
  useCurrentSelection,
  type SelectionKind,
} from '../../../shared/selection/useSelectionStore.js';
import type { NoteLinkInput } from '../api/notes.client.js';
import { useCreateNoteMutation } from '../hooks/useNotes.js';
import { useCurrentEntityContext } from '../hooks/useCurrentEntityContext.js';

/** Привязка заметки к выбранной строке списка. */
function selectionLink(kind: SelectionKind, id: string): NoteLinkInput {
  switch (kind) {
    case 'deal':
      return { dealId: id };
    case 'contact':
      return { contactId: id };
    case 'company':
      return { companyId: id };
    case 'task':
      return { taskId: id };
    case 'meeting':
      return { calendarEventId: id };
    case 'document':
      return { documentId: id };
    case 'template':
      return { templateId: id };
  }
}

const SELECTION_NOUN: Record<SelectionKind, string> = {
  get deal() {
    return i18n.t('crm.features.map.features.notes.components.quicknotebutton.62');
  },
  get contact() {
    return i18n.t('crm.features.map.features.notes.components.quicknotebutton.63');
  },
  get company() {
    return i18n.t('crm.features.map.features.notes.components.quicknotebutton.64');
  },
  get task() {
    return i18n.t('crm.features.map.features.notes.components.quicknotebutton.65');
  },
  get meeting() {
    return i18n.t('crm.features.map.features.notes.components.quicknotebutton.66');
  },
  get document() {
    return i18n.t('crm.features.map.features.notes.components.quicknotebutton.67');
  },
  get template() {
    return i18n.t('crm.features.map.features.notes.components.quicknotebutton.68');
  },
};

export interface NoteCreateModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Модалка создания заметки. Поведение прежнее: по умолчанию цепляет заметку к
 * сущности, открытой на экране (сделка/контакт/компания), иначе — к отмеченным
 * строкам списка, а без всего этого создаёт заметку без привязки.
 */
export function NoteCreateModal({ open, onClose }: NoteCreateModalProps): ReactElement {
  useI18nSubscription();
  const entity = useCurrentEntityContext();
  const selection = useCurrentSelection();
  const [body, setBody] = useState('');
  const [attach, setAttach] = useState(true);
  const createMut = useCreateNoteMutation();

  // Сбрасываем поля только в момент открытия. Зависеть от createMut нельзя:
  // объект мутации меняется между рендерами и стирал бы набранный текст.
  const resetMutation = createMut.reset;
  useEffect(() => {
    if (open) {
      setBody('');
      setAttach(true);
      resetMutation();
    }
  }, [open, resetMutation]);

  const hasEntity = entity.kind !== null;
  // Карточка не открыта, но в списке отмечены строки — цепляем к ним.
  const hasSelection = !hasEntity && selection.kind !== null && selection.items.length > 0;
  const entityName = entity.title ?? '…';
  const selectionNoun = selection.kind ? SELECTION_NOUN[selection.kind] : '';

  const submit = (): void => {
    const text = body.trim();
    if (!text) return;

    // Одна заметка с несколькими привязками — строки в списке не дублируются.
    if (hasSelection && attach && selection.kind) {
      const kind = selection.kind;
      createMut.mutate(
        { body: text, links: selection.items.map((item) => selectionLink(kind, item.id)) },
        { onSuccess: onClose },
      );
      return;
    }

    createMut.mutate(
      { body: text, ...(hasEntity && attach ? { links: [entity.link] } : {}) },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title={i18n.t('crm.features.features.notes.components.quicknotebutton.быстрая_заметка_2')}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={i18n.t('crm.features.features.notes.components.quicknotebutton.сохранить')}
      cancelText={i18n.t('crm.features.features.notes.components.quicknotebutton.отмена')}
      confirmLoading={createMut.isPending}
      okButtonProps={{ disabled: body.trim().length === 0 }}
      width={520}
      destroyOnHidden
    >
      {createMut.isError ? (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          title={i18n.t(
            'crm.features.features.notes.components.quicknotebutton.не_удалось_сохранить_заметку',
          )}
          description={(createMut.error as Error).message}
        />
      ) : null}

      {/* Quick template slash chips */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-stone-400 text-[11px]">{i18n.t('crm.notes.templates')}</span>
        <button
          type="button"
          onClick={() =>
            setBody((prev) => (prev ? `${prev}\n\n` : '') + i18n.t('crm.notes.tpl_summary'))
          }
          className="rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:border-amber-400 hover:bg-amber-50/50 transition cursor-pointer"
        >
          /summary
        </button>
        <button
          type="button"
          onClick={() =>
            setBody((prev) => (prev ? `${prev}\n\n` : '') + i18n.t('crm.notes.tpl_decision'))
          }
          className="rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:border-amber-400 hover:bg-amber-50/50 transition cursor-pointer"
        >
          /decision
        </button>
        <button
          type="button"
          onClick={() =>
            setBody((prev) => (prev ? `${prev}\n\n` : '') + i18n.t('crm.notes.tpl_task'))
          }
          className="rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:border-amber-400 hover:bg-amber-50/50 transition cursor-pointer"
        >
          /task
        </button>
      </div>

      <Input.TextArea
        autoFocus
        rows={5}
        maxLength={10_000}
        placeholder={i18n.t('crm.features.features.notes.components.quicknotebutton.что_записать')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          // Ctrl/Cmd+Enter — сохранить, не отрывая рук от клавиатуры.
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      />

      <div className="mt-3 text-sm">
        {hasEntity ? (
          <Checkbox checked={attach} onChange={(e) => setAttach(e.target.checked)}>
            {i18n.t('crm.features.features.notes.components.quicknotebutton.привязать_к')}
            {entity.typeLabel}: <EntityStatusChip tone="default">{entityName}</EntityStatusChip>
          </Checkbox>
        ) : hasSelection ? (
          <div className="flex flex-col gap-2">
            {/* Подпись должна совпадать с поведением `submit`: создаётся ОДНА
                заметка с несколькими привязками, а не по заметке на строку. */}
            <Checkbox checked={attach} onChange={(e) => setAttach(e.target.checked)}>
              {i18n.t(
                'crm.features.features.notes.components.quicknotebutton.привязать_к_выбранным',
              )}
              {selection.items.length}
              {i18n.t('crm.features.features.notes.components.quicknotebutton.одна_заметка_на_все')}
            </Checkbox>
            <div className="flex flex-wrap gap-1">
              {selection.items.slice(0, 6).map((item) => (
                <EntityStatusChip key={item.id} tone="default">
                  {item.label}
                </EntityStatusChip>
              ))}
              {selection.items.length > 6 ? (
                <Tag>
                  {i18n.t('crm.features.features.notes.components.quicknotebutton.еще')}
                  {selection.items.length - 6}
                </Tag>
              ) : null}
            </div>
          </div>
        ) : (
          <Typography.Text type="secondary">
            {i18n.t(
              'crm.features.features.notes.components.quicknotebutton.ничего_не_открыто_и_не_выбрано_заметка_сох',
            )}
            {selectionNoun ||
              i18n.t('crm.features.features.notes.components.quicknotebutton.записям')}
            .
          </Typography.Text>
        )}
      </div>
    </Modal>
  );
}
